import { useState, useEffect, useCallback, useRef } from 'react';
import { useEventBus, getRobotEvent, ROBOT_EVENTS, nomalizeRobotTopicName } from 'shared/services/eventBus';

/**
 * Read a dot-separated path ("battery.battery_level") from an object.
 * @private
 */
const getByPath = (obj, path) =>
  path.split('.').reduce((acc, key) => (acc == null ? undefined : acc[key]), obj);

/**
 * Compare two values found at a compare-key path. Objects/arrays are compared
 * structurally because transformFn builds fresh objects on every message.
 * @private
 */
const isSameValue = (a, b) => {
  if (Object.is(a, b)) return true;
  if (a != null && b != null && typeof a === 'object' && typeof b === 'object') {
    return JSON.stringify(a) === JSON.stringify(b);
  }
  return false;
};

/**
 * useRobotStatus - Hook to subscribe to robot status updates
 *
 * Features:
 * - Automatic subscription to robot status events (robot:<id>:<topic>)
 * - Throttled state management for robot status
 * - Automatic cleanup on unmount
 * - Optional status change callback
 *
 * @param {string} robotId - Robot identifier (peer id the RobotPeerConnectionProvider publishes under)
 * @param {Object} [options] - Hook options. Memoize it (useMemo) so the subscription is not recreated every render.
 * @param {string} [options.topic_name] - Robot topic to listen to, e.g. "robot.status". It is normalized the same
 *   way RobotConnectionContext normalizes incoming topics, so "/robot/status" and "robot/status" both work.
 *   Defaults to ROBOT_EVENTS.STATUS_UPDATED ("robot.status", the standard status message).
 * @param {Function} [options.transformFn] - Maps the raw topic value to the status object stored in state.
 *   Returning a falsy value drops the message.
 * @param {Array<string>} [options.compareKeys] - Dot-paths of the (transformed) status that must change for a
 *   re-render, e.g. ['robot_state.base_state', 'derived.battery_percent']. Defaults to a structural
 *   comparison of the whole status.
 * @param {number} [options.throttle=100] - Minimum interval between state updates in ms
 * @param {Function} [options.onStatusChange] - Callback invoked with the new status after each state update
 * @param {*} [options.initialStatus=null] - Initial status value
 * @returns {Object} { status }
 *
 * @example
 * const options = useMemo(() => ({
 *   topic_name: STATUS_TOPICS.ROBOT_STATUS,
 *   transformFn: normalizeRobotStatus,
 *   compareKeys: ROBOT_STATUS_COMPARE_KEYS,
 * }), []);
 * const { status } = useRobotStatus('robot-123', options);
 *
 * // Access status fields (normalized robot.status: robot_info / robot_state / mission_info / derived)
 * const batteryLevel = status?.derived?.battery_percent;
 */
export const useRobotStatus = (robotId, options = {}) => {
  const eventBus = useEventBus();

  const [status, setStatus] = useState(options.initialStatus || null);

  // Robot status throttling
  const robotStatusUpdateRef = useRef(null);
  const [robotStatusThrottleDelay] = useState(options.throttle || 100); // 100ms throttle for robot status
  const latestRobotStatusRef = useRef(null);
  const lastRobotStatusRef = useRef(null);

  // Keep the latest callback in a ref so a new callback identity never re-subscribes
  const onStatusChangeRef = useRef(options.onStatusChange);
  useEffect(() => {
    onStatusChangeRef.current = options.onStatusChange;
  }, [options.onStatusChange]);

  const compareKeys = options.compareKeys;
  const transformFn = options.transformFn;
  const topicName = options.topic_name
    ? nomalizeRobotTopicName(options.topic_name)
    : ROBOT_EVENTS.STATUS_UPDATED;

  // ===== OPTIMIZED STATUS COMPARISON =====
  const shouldUpdateRobotStatus = useCallback((newStatus, currentStatus) => {
    if (!currentStatus) return true;

    if (Array.isArray(compareKeys) && compareKeys.length > 0) {
      return compareKeys.some(
        (path) => !isSameValue(getByPath(newStatus, path), getByPath(currentStatus, path))
      );
    }

    // Default: structural comparison of the whole (transformed) status
    return !isSameValue(newStatus, currentStatus);
  }, [compareKeys]);

  // ===== THROTTLED ROBOT STATUS UPDATE =====
  const updateRobotStatusThrottled = useCallback(
    (robotInfo) => {
      latestRobotStatusRef.current = robotInfo;

      if (robotStatusUpdateRef.current) {
        return; // Already have a pending update
      }

      robotStatusUpdateRef.current = setTimeout(() => {
        robotStatusUpdateRef.current = null;
        const latestStatus = latestRobotStatusRef.current;
        if (
          latestStatus &&
          shouldUpdateRobotStatus(latestStatus, lastRobotStatusRef.current)
        ) {
          setStatus(latestStatus);
          lastRobotStatusRef.current = latestStatus;
          onStatusChangeRef.current?.(latestStatus);
        }
      }, robotStatusThrottleDelay);
    },
    [robotStatusThrottleDelay, shouldUpdateRobotStatus]
  );

  // Handle status updates
  const handleStatusUpdate = useCallback((newStatus) => {
    const transformedStatus = transformFn ? transformFn(newStatus) : newStatus;
    if (transformedStatus) {
      updateRobotStatusThrottled(transformedStatus);
    }
  }, [transformFn, updateRobotStatusThrottled]);

  // Forget the previous robot's status when the subscription target changes
  useEffect(() => {
    lastRobotStatusRef.current = null;
    latestRobotStatusRef.current = null;
    setStatus(options.initialStatus || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [robotId, topicName]);

  useEffect(() => {
    if (!robotId) {
      return;
    }

    // Subscribe to robot-specific status updates (robot:<id>:<topic>)
    const eventName = getRobotEvent(robotId, topicName);
    const unsubscribe = eventBus.subscribe(eventName, handleStatusUpdate);

    // Cleanup: drop the subscription and any pending throttled update
    return () => {
      unsubscribe();
      if (robotStatusUpdateRef.current) {
        clearTimeout(robotStatusUpdateRef.current);
        robotStatusUpdateRef.current = null;
      }
    };
  }, [eventBus, robotId, topicName, handleStatusUpdate]);

  return {
    status,
  };
};

/**
 * useRobotConnectionStatus - Hook to subscribe to robot connection state
 *
 * @param {string} robotId - Robot identifier
 * @returns {Object} { connectionState, onlineStatus, lastUpdate }
 *
 * @example
 * const { connectionState, onlineStatus } = useRobotConnectionStatus('robot-123');
 */
export const useRobotConnectionStatus = (robotId) => {
  const eventBus = useEventBus();
  const [connectionState, setConnectionState] = useState(null);
  const [onlineStatus, setOnlineStatus] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(null);

  useEffect(() => {
    if (!robotId) {
      return;
    }

    // Subscribe to connection state changes
    const connectionEventName = getRobotEvent(robotId, ROBOT_EVENTS.CONNECTION_STATE);
    const onlineEventName = getRobotEvent(robotId, ROBOT_EVENTS.ONLINE_STATUS_CHANGED);

    const unsubscribeConnection = eventBus.subscribe(connectionEventName, (state) => {
      setConnectionState(state);
      setLastUpdate(Date.now());
    });

    const unsubscribeOnline = eventBus.subscribe(onlineEventName, (status) => {
      setOnlineStatus(status);
      setLastUpdate(Date.now());
    });

    // Cleanup
    return () => {
      unsubscribeConnection();
      unsubscribeOnline();
    };
  }, [eventBus, robotId]);

  return {
    connectionState,
    onlineStatus,
    lastUpdate
  };
};

/**
 * useMultipleRobotStatus - Hook to subscribe to status updates from multiple robots
 *
 * @param {Array<string>} robotIds - Array of robot identifiers
 * @returns {Object} Map of robotId to status { [robotId]: status }
 *
 * @example
 * const statuses = useMultipleRobotStatus(['robot-1', 'robot-2', 'robot-3']);
 * const robot1Battery = statuses['robot-1']?.robot_state?.battery_state?.percentage;
 */
export const useMultipleRobotStatus = (robotIds) => {
  const eventBus = useEventBus();
  const [statuses, setStatuses] = useState({});

  useEffect(() => {
    if (!robotIds || robotIds.length === 0) {
      return;
    }

    const unsubscribers = [];

    // Subscribe to each robot's status
    robotIds.forEach(robotId => {
      const eventName = getRobotEvent(robotId, ROBOT_EVENTS.STATUS_UPDATED);
      const unsubscribe = eventBus.subscribe(eventName, (status) => {
        setStatuses(prev => ({
          ...prev,
          [robotId]: status
        }));
      });
      unsubscribers.push(unsubscribe);
    });

    // Cleanup
    return () => {
      unsubscribers.forEach(unsub => unsub());
    };
  }, [eventBus, robotIds]);

  return statuses;
};

/**
 * useRobotStatusField - Hook to subscribe to a specific field in robot status
 *
 * @param {string} robotId - Robot identifier
 * @param {string} fieldName - Top-level field of the robot.status value to track (e.g., 'robot_state')
 * @param {*} [defaultValue] - Default value if field not present
 * @returns {*} Field value
 *
 * @example
 * const robotState = useRobotStatusField('robot-123', 'robot_state', null);
 * const missionInfo = useRobotStatusField('robot-123', 'mission_info', null);
 */
export const useRobotStatusField = (robotId, fieldName, defaultValue = null) => {
  const { status } = useRobotStatus(robotId);
  return status?.[fieldName] ?? defaultValue;
};

export default useRobotStatus;
